import { Box } from '@mui/material';
import styles from './SelectAssignee.module.scss';
import classNames from 'classnames/bind';
import { PlusIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import BaseImage from '~/components/common/base-image/BaseImage';
import { memo, useEffect, useState } from 'react';
import { ISelectAccount } from '~/utils/interface/account';

export type SelectAssigneeProps = {
  name?: string;
  options: Array<ISelectAccount>;
  value?: Array<string>;
  onChange?: (...event: any[]) => void;
  disabled?: boolean;
};

const cx = classNames.bind(styles);

const SelectAssignee = (props: SelectAssigneeProps) => {
  //#region Destructuring Props
  const { disabled, ...restProps } = props;
  //#endregion Destructuring Props

  //#region Declare State
  const [value, setValue] = useState<Array<string>>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.value) setValue(props.value);
  }, [props.value]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderSelectISelectAccountComponent = (item: ISelectAccount) => {
    return (
      <Box className='flex justify-center items-center gap-[10px] p-[6px_10px] w-full'>
        <Box>
          <BaseImage src={item.avatar} className='w-[30px] h-[30px] rounded-full object-contain' />
        </Box>
        <Box>
          <Box className='text-[#222732] text-[12px] leading-[14.4px] font-medium'>{item.name}</Box>
          <Box className='text-[#9AA4BA] text-[10px] leading-[12px]'>{item.email}</Box>
        </Box>
        {!disabled && (
          <Box className='ml-auto'>
            <PlusIcon className='[&>path]:stroke-[#292D32] w-[10px] h-[10px]' />
          </Box>
        )}
      </Box>
    );
  };

  const renderSelectedOptionComponent = (item: ISelectAccount | Array<ISelectAccount>) => {
    if (!Array.isArray(item)) return <></>;
    const displayedItems: Array<ISelectAccount> = item.slice(0, 3);

    return (
      <Box className={cx('event-assignees-container')}>
        {displayedItems.map((item: ISelectAccount, index: number) => {
          return (
            <Box key={index} className={cx('event-assignee-container')}>
              <Box className={cx('event-assignee', 'border-[2px]', 'border-[#FFFFFF]')}>
                <BaseImage src={item.avatar} className='rounded-full object-contain' />
              </Box>
            </Box>
          );
        })}
        {!disabled && (
          <Box className={cx('event-assignee-container')}>
            <Box className={cx('event-assignee', 'event-assignee-more', 'border-[2px]', 'border-[#FFFFFF]')}>
              <PlusIcon className='w-[6px]' />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const rendePlaceHolderComponent = () => {
    return (
      <Box className={cx('event-assignees-container')}>
        <Box className={cx('event-assignee-container')}>
          <Box className={cx('event-assignee', 'event-assignee-more', 'border-[2px]', 'border-[#FFFFFF]')}>
            <PlusIcon className='w-[6px]' />
          </Box>
        </Box>
      </Box>
    );
  };

  //#endregion Handle Function

  return (
    <>
      <BaseSelect
        {...restProps}
        onChange={(e) => {
          if (!disabled) {
            setValue(e.target.value);
            if (props.onChange) {
              props.onChange(e);
            }
          }
        }}
        value={value}
        renderPlaceholder={() => rendePlaceHolderComponent()}
        menuClassName={cx('select-assignee-menu')}
        className={cx('select-assignee')}
        options={props.options}
        renderSelected={(item: ISelectAccount | Array<ISelectAccount>) => renderSelectedOptionComponent(item)}
        renderOptionItem={(item: ISelectAccount) => renderSelectISelectAccountComponent(item)}
        multiple
        isSearchEnable={true}
        disabled={disabled}
      />
    </>
  );
};

export default memo(SelectAssignee) as typeof SelectAssignee;
